/**
 * EraserSurface — surface de peinture de la gomme, montée sur le calque image
 * sélectionné quand l'outil Gomme est actif.
 *
 * TROIS SURFACES, TROIS RÔLES
 *  1. `workRef`  — canvas hors écran portant le masque à sa pleine définition.
 *                  C'est lui qui sera sérialisé dans `el.eraseMask`.
 *  2. `baseRef`  — l'image source, pré-rééchantillonnée UNE FOIS à la taille
 *                  d'affichage. Redimensionner une image de 4000 px à chaque
 *                  geste coûterait bien plus cher que de recopier un bitmap
 *                  déjà à la bonne taille.
 *  3. `viewRef`  — canvas visible. Chaque rafraîchissement = deux recopies de
 *                  même taille (base, puis masque en `destination-in`).
 *
 * Le masque n'est sérialisé dans le store qu'au RELÂCHÉ. Le faire à chaque
 * déplacement imposerait un `toDataURL` sur 1400 px, une écriture dans le
 * store et un décodage d'image par le navigateur — plusieurs dizaines de
 * millisecondes par événement, soit un geste haché.
 *
 * RIEN DE TOUT CELA NE PASSE PAR L'ÉTAT REACT PENDANT LE GESTE.
 * Le curseur est déplacé en écrivant directement dans le style du nœud, et la
 * peinture est cadencée sur l'affichage via requestAnimationFrame. Une souris
 * émet jusqu'à 120 événements par seconde : y répondre par un `setState`
 * reviendrait à demander 120 rendus React par seconde pour bouger un rond.
 *
 * REPÈRE
 * Les coordonnées passent de l'écran au masque par rotation inverse autour du
 * centre du calque, puis normalisation. Le retournement (scaleX/scaleY à -1)
 * est appliqué en dernier : CSS masque l'image AVANT sa transformation, donc
 * le masque vit dans le repère non retourné. Cette arithmétique est partagée
 * avec la sélection de zone (`coordsBoite` dans utils/selection) : elle s'était
 * déjà trompée une fois, elle ne doit exister qu'à un seul endroit.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLabelStore } from '../../store/useLabelStore';
import { maskSize, openMaskCanvas, punchDisc, punchSegment, loadImage } from '../../utils/eraseMask';
import { loadImageWithRefresh, galleryIdFromUrl } from '../../utils/galleryUrl';
import { coordsBoite } from '../../utils/selection';

// Définition du canvas d'aperçu : au-delà, on rééchantillonne pour rien.
const VIEW_MAX_PX = 1200;

export default function EraserSurface({ el, scale }) {
    const eraser = useLabelStore((s) => s.eraser);

    const workRef = useRef(null);   // masque pleine définition (hors écran)
    const baseRef = useRef(null);   // image pré-rééchantillonnée (hors écran)
    const viewRef = useRef(null);   // canvas visible
    const imgRef = useRef(null);    // image source décodée
    const lastRef = useRef(null);   // dernier point peint, en coordonnées masque
    const dirtyRef = useRef(false); // un trait a-t-il modifié le masque ?
    const ownWriteRef = useRef(null); // dernier masque écrit PAR nous
    const rafRef = useRef(0);       // rafraîchissement en attente
    const cursorRef = useRef(null); // nœud DOM du curseur rond

    const [ready, setReady] = useState(false);
    // Échec de chargement de l'image du calque. Il était AVALÉ : la gomme
    // s'ouvrait sur une surface vide sans un mot, alors que la cause est
    // presque toujours la même — l'URL signée du stockage n'est valable qu'une
    // heure. On la réémet, et si cela ne suffit pas, on le DIT.
    const [erreurImage, setErreurImage] = useState(null);

    const { w: maskW, h: maskH } = maskSize(el);

    // (Re)chargement à chaque changement de calque, d'image ou de masque
    // externe (undo, « Tout restaurer »).
    useEffect(() => {
        // Le masque qu'on vient nous-même d'écrire est déjà dans `workRef` :
        // le recharger ne ferait que redécoder une image pour un résultat
        // identique, avec un clignotement à la clé.
        if (el.eraseMask && el.eraseMask === ownWriteRef.current) return;
        let alive = true;
        setReady(false);
        setErreurImage(null);
        (async () => {
            const [work, chargee] = await Promise.all([
                openMaskCanvas(el),
                // Un échec est réessayé avec une URL fraîche : l'URL signée du
                // stockage expire au bout d'une heure, et c'est de loin la
                // cause la plus fréquente. La nouvelle URL est mémorisée dans
                // le calque, sinon chaque remontage referait l'aller-retour.
                loadImageWithRefresh(el.url, loadImage, (fraiche) => {
                    useLabelStore.getState().updateElement(el.id, { url: fraiche });
                }),
            ]);
            if (!alive) return;
            workRef.current = work;
            imgRef.current = chargee.img;
            if (!chargee.img) {
                setErreurImage(
                    galleryIdFromUrl(el.url)
                        ? "Image du calque illisible : le lien vers la galerie a expiré et n'a pas pu être renouvelé. Rechargez la page."
                        : "Image du calque illisible."
                );
            }
            rebuildBase();
            setReady(true);
            repaint();
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [el.id, el.url, el.eraseMask, maskW, maskH]);

    // Le canvas visible suit la taille écran du calque.
    useLayoutEffect(() => {
        const cvs = viewRef.current;
        if (!cvs) return;
        const k = Math.min(1, VIEW_MAX_PX / Math.max(el.width * scale, el.height * scale, 1));
        cvs.width = Math.max(2, Math.round(el.width * scale * k));
        cvs.height = Math.max(2, Math.round(el.height * scale * k));
        rebuildBase();
        repaint();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [el.width, el.height, scale, ready]);

    useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

    /** Pré-rend l'image cadrée « contain » à la taille exacte du canvas visible. */
    function rebuildBase() {
        const view = viewRef.current;
        const img = imgRef.current;
        if (!view || !img) return;
        const c = baseRef.current || (baseRef.current = document.createElement('canvas'));
        c.width = view.width;
        c.height = view.height;
        const ctx = c.getContext('2d');
        const iw = img.naturalWidth || img.width;
        const ih = img.naturalHeight || img.height;
        const s = Math.min(c.width / iw, c.height / ih);
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.drawImage(img, (c.width - iw * s) / 2, (c.height - ih * s) / 2, iw * s, ih * s);
    }

    /** Recompose le canvas visible : image pré-rendue, puis masque. */
    function repaint() {
        const cvs = viewRef.current;
        const work = workRef.current;
        if (!cvs || !work) return;
        const ctx = cvs.getContext('2d');
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, cvs.width, cvs.height);
        if (baseRef.current) ctx.drawImage(baseRef.current, 0, 0);
        ctx.globalCompositeOperation = 'destination-in';
        // Le masque est bien plus défini que l'aperçu (jusqu'à 1400 px contre
        // ~150 à l'écran) : son rééchantillonnage est le poste le plus lourd de
        // la boucle. En qualité basse il reste indiscernable à cette taille.
        ctx.imageSmoothingQuality = 'low';
        ctx.drawImage(work, 0, 0, cvs.width, cvs.height);
        ctx.globalCompositeOperation = 'source-over';
    }

    /** Un seul rafraîchissement par image affichée, quel que soit le débit souris. */
    function scheduleRepaint() {
        if (rafRef.current) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = 0;
            repaint();
        });
    }

    /** Point écran → coordonnées du masque (rotation et retournement compris). */
    function toMask(clientX, clientY) {
        const host = viewRef.current;
        if (!host) return null;
        const { u, v } = coordsBoite(el, scale, host.getBoundingClientRect(), clientX, clientY);
        return { x: u * maskW, y: v * maskH };
    }

    /** Rayon de gomme, des millimètres du document aux pixels du masque. */
    const maskRadius = () => (eraser.radius / Math.max(0.01, el.width)) * maskW;

    /** Déplace le curseur rond sans repasser par React. */
    function moveCursor(clientX, clientY) {
        const n = cursorRef.current;
        if (!n) return;
        n.style.transform = `translate3d(${clientX}px, ${clientY}px, 0) translate(-50%, -50%)`;
        n.style.opacity = '1';
    }

    const commit = () => {
        if (!dirtyRef.current || !workRef.current) return;
        dirtyRef.current = false;
        const dataUrl = workRef.current.toDataURL('image/png');
        ownWriteRef.current = dataUrl;
        useLabelStore.getState().updateElement(el.id, { eraseMask: dataUrl });
        useLabelStore.getState()._pushState('Gomme');
    };

    const onPointerDown = (e) => {
        if (e.button !== 0 || !ready) return;
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        const p = toMask(e.clientX, e.clientY);
        if (!p) return;
        punchDisc(workRef.current.getContext('2d'), p.x, p.y, maskRadius(), eraser.hardness);
        lastRef.current = p;
        dirtyRef.current = true;
        scheduleRepaint();
    };

    const onPointerMove = (e) => {
        moveCursor(e.clientX, e.clientY);
        if (!lastRef.current || !ready) return;
        e.preventDefault();
        e.stopPropagation();
        const ctx = workRef.current.getContext('2d');
        const r = maskRadius();

        // Les points intermédiaires que le navigateur a regroupés dans cet
        // événement : on les peint TOUS (le tracé reste fidèle à la main) mais
        // on ne rafraîchit l'écran qu'une fois.
        const pts = e.nativeEvent.getCoalescedEvents?.() || [];
        const seq = pts.length ? pts : [e];
        for (const pt of seq) {
            const p = toMask(pt.clientX, pt.clientY);
            if (!p) continue;
            punchSegment(ctx, lastRef.current.x, lastRef.current.y, p.x, p.y, r, eraser.hardness);
            lastRef.current = p;
        }
        dirtyRef.current = true;
        scheduleRepaint();
    };

    const endStroke = (e) => {
        if (!lastRef.current) return;
        e?.stopPropagation();
        lastRef.current = null;
        repaint();
        commit();
    };

    // Un relâché hors du calque doit tout de même clore le trait, sans quoi le
    // masque resterait non enregistré jusqu'au geste suivant.
    useEffect(() => {
        const onUp = () => { if (lastRef.current) { lastRef.current = null; commit(); } };
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointerup', onUp);
            onUp(); // démontage en plein trait (changement d'outil) : ne rien perdre
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [el.id]);

    // Diamètre du curseur rond, à l'échelle EXACTE de ce qui sera effacé.
    // AVANT : `Math.max(4, ...)`. Ce plancher de 4 px mentait dès que le zoom
    // était faible et le rayon petit : le rond dessiné devenait plus grand que
    // l'effacement réel, et l'utilisateur croyait gommer une zone plus large
    // qu'il ne le faisait. La visibilité du pointeur ne dépend de toute façon
    // pas du rond : la pastille centrale, de taille fixe et contournée de noir,
    // reste repérable même quand le cercle se réduit à un cheveu.
    const diameter = eraser.radius * scale * 2;

    return (
        <>
            <canvas
                ref={viewRef}
                data-no-export="true"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endStroke}
                onPointerEnter={(e) => moveCursor(e.clientX, e.clientY)}
                onPointerLeave={() => { if (cursorRef.current) cursorRef.current.style.opacity = '0'; }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                className="absolute inset-0 w-full h-full"
                style={{ cursor: 'none', touchAction: 'none' }}
            />
            {/* L'image n'a pas pu être chargée, même après réémission du lien.
                On le DIT sur la surface : sans ce message, la gomme s'ouvrait
                sur un vide et l'utilisateur croyait l'outil cassé. Couleurs
                littérales, comme le curseur : ce panneau est posé PAR-DESSUS une
                image quelconque, la polarité du thème n'y garantit rien. */}
            {erreurImage && (
                <div
                    role="alert"
                    className="absolute inset-0 flex items-center justify-center p-3 text-center pointer-events-none bg-[#0b0b0ed1]"
                >
                    <span className="text-[10px] font-mono leading-relaxed max-w-[90%] text-red-300">
                        {erreurImage}
                    </span>
                </div>
            )}
            {/* Curseur rond, à l'échelle réelle du geste.
                RENDU EN PORTAL, et c'est indispensable : un `position: fixed`
                placé sous un ancêtre qui porte un `transform` n'est plus relatif
                au viewport mais à cet ancêtre. Or le canvas de l'éditeur est
                justement mis à l'échelle — le cercle s'y retrouvait décalé et
                multiplié par le zoom, donc invisible.
                Double contour clair sur sombre pour rester lisible sur n'importe
                quelle image, et une croix centrale pour viser au pixel. */}
            {createPortal(
                <div
                    ref={cursorRef}
                    aria-hidden="true"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: diameter,
                        height: diameter,
                        borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.95)',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(0,0,0,0.35)',
                        pointerEvents: 'none',
                        opacity: 0,
                        zIndex: 65,
                        willChange: 'transform',
                    }}
                >
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: 5,
                        height: 5,
                        marginTop: -2.5,
                        marginLeft: -2.5,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.9)',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.6)',
                    }} />
                </div>,
                document.body
            )}
        </>
    );
}
